// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { RefreshCw, TriangleAlert } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { InlineSpinner } from "@/components/shared/inline-spinner";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * Header for one panel: title, description, and a refresh scoped to that panel.
 *
 * Same treatment as the footer control that reloads a whole page of panels — one refresh
 * affordance, one look, whatever its scope.
 */
export function PanelHeader({
  title,
  description,
  onRefresh,
  isRefreshing,
}: {
  title: string;
  // A node, not a string: a description may name something as a link.
  description: ReactNode;
  onRefresh: () => void;
  isRefreshing: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="space-y-1">
        <h3 className="font-medium text-sm">{title}</h3>
        <p className="text-muted-foreground text-sm">{description}</p>
      </div>
      <Button
        variant="outline"
        size="icon"
        className="h-9 w-9 shrink-0"
        onClick={onRefresh}
        disabled={isRefreshing}
        aria-label={t("panel.refresh", { panel: title })}
      >
        <RefreshCw className={cn("h-4 w-4", isRefreshing && "animate-spin")} />
      </Button>
    </div>
  );
}

/** Shared failure state — each panel owns its own, so one outage stays local. */
export function PanelError({ onRetry, isRetrying }: { onRetry: () => void; isRetrying: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2">
      <span className="flex items-center gap-2 text-sm text-destructive">
        <TriangleAlert className="h-4 w-4 shrink-0" />
        {t("panel.error")}
      </span>
      <Button variant="outline" size="sm" onClick={onRetry} disabled={isRetrying}>
        {isRetrying ? <InlineSpinner /> : <RefreshCw className="h-3.5 w-3.5" />}
        {t("panel.retry")}
      </Button>
    </div>
  );
}

// Rules go between panels, never above the one that opens a column: `*+*` is every
// panel that has one above it inside the same column.
const PANEL_STACK = "flex-1 space-y-6 [&>*+*]:border-t [&>*+*]:pt-6";

// Half the gutter each, so a rule runs out to the seam and the two meet in a T
// instead of the horizontal one stopping 24px short of the vertical. The margin
// widens the box into the gutter; the matching padding puts the content back.
const RULE_TO_SEAM = ["xl:[&>*+*]:-mr-6 xl:[&>*+*]:pr-6", "xl:[&>*+*]:-ml-6 xl:[&>*+*]:pl-6"];

// The seam between the columns, as tall as the taller of the two. A border on the
// second column would stop where that column's content does, which on a page whose
// whole point is uneven columns is exactly where it must not stop.
const PANEL_LAYOUT = cn(
  "flex flex-col gap-6 xl:flex-row xl:gap-12",
  "xl:relative xl:after:absolute xl:after:top-0 xl:after:bottom-0 xl:after:left-1/2 xl:after:w-px xl:after:bg-border",
);

// `-bottom-4` runs the seam through a parent's `space-y-4` so it lands on a separator
// below, closing the T at the bottom the same way the panel rules close theirs at the
// sides. The gap itself is untouched — only the line grows.
const SEAM_TO_SEPARATOR = "xl:after:-bottom-4";

// Stacked on a narrow screen the two columns run together, so the second one needs
// the rule its first panel is deliberately missing.
const SECOND_COLUMN_STACKED = "border-t pt-6 xl:border-t-0 xl:pt-0";

/**
 * Panels dealt into two columns split by a full-height seam, with rules between the panels of
 * a column. Side by side from `xl`, stacked below it.
 *
 * Columns are passed pre-split rather than flowed by CSS `columns`, so the caller decides which
 * panel opens each column — and so which panels are kept free of a rule above them.
 */
export function PanelColumns({
  columns,
  seamToSeparator = false,
}: {
  columns: [ReactNode[], ReactNode[]];
  /** Extend the seam down through a `space-y-4` gap onto a `Separator` that follows. */
  seamToSeparator?: boolean;
}) {
  return (
    <div className={cn(PANEL_LAYOUT, seamToSeparator && SEAM_TO_SEPARATOR)}>
      {columns.map((panels, columnIndex) => (
        <div
          key={columnIndex}
          className={cn(PANEL_STACK, RULE_TO_SEAM[columnIndex], columnIndex > 0 && SECOND_COLUMN_STACKED)}
        >
          {panels}
        </div>
      ))}
    </div>
  );
}
